import { LicenseCheck } from '../types/license';

const FUNC_ENDPOINT = process.env.REACT_APP_FUNC_ENDPOINT || '';

// Cache license check results to avoid repeated API calls
let cachedLicense: LicenseCheck | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function checkLicense(
  userEmail?: string,
  tenantId?: string,
  taskCount: number = 0
): Promise<LicenseCheck> {
  // Return cached result if still valid
  const now = Date.now();
  if (cachedLicense && (now - cacheTimestamp) < CACHE_DURATION) {
    // Update canEdit based on current task count for free tier
    if (cachedLicense.plan === 'free' || cachedLicense.isExpired) {
      const maxTasks = cachedLicense.restrictions.maxTasksPerBoard || 10;
      cachedLicense.restrictions.canEdit = taskCount < maxTasks;
    }
    return cachedLicense;
  }

  const params = new URLSearchParams({
    taskCount: taskCount.toString(),
  });

  if (userEmail) {
    params.append('userEmail', userEmail);
  }
  if (tenantId) {
    params.append('tenantId', tenantId);
  }

  try {
    const response = await fetch(`${FUNC_ENDPOINT}/license/check?${params}`);

    if (!response.ok) {
      throw new Error(`License check failed: ${response.statusText}`);
    }

    const license: LicenseCheck = await response.json();

    // Cache the result
    cachedLicense = license;
    cacheTimestamp = now;

    return license;
  } catch (error) {
    console.error('Error checking license:', error);
    // Return free tier on error to not block users
    return getFreeTierDefaults(taskCount);
  }
}

export function getFreeTierDefaults(taskCount: number = 0): LicenseCheck {
  return {
    hasLicense: false,
    plan: 'free',
    isExpired: false,
    restrictions: {
      maxTasksPerBoard: 10,
      canUploadFiles: false,
      canEdit: taskCount < 10,
    },
  };
}

export function clearLicenseCache(): void {
  cachedLicense = null;
  cacheTimestamp = 0;
}

// Helper to check if user can add more tasks
export function canAddTask(license: LicenseCheck, currentTaskCount: number): boolean {
  if (license.restrictions.maxTasksPerBoard === null) {
    return true; // Unlimited
  }
  return currentTaskCount < license.restrictions.maxTasksPerBoard;
}

// Helper to check if user can upload files
export function canUploadFiles(license: LicenseCheck): boolean {
  return license.restrictions.canUploadFiles;
}

// Helper to check if user can edit the board
export function canEditBoard(license: LicenseCheck): boolean {
  return license.restrictions.canEdit;
}
